import React, { useContext, useMemo } from 'react'

import { Link } from 'react-router-dom'

import {
  Box,
  Health,
  List,
  ListItem,
  makeAppStyles,
  Typography,
} from '@percept/mui'

import {  OpenInNew } from '@percept/mui/icons'

import { CampaignSetupView } from './CampaignSetupView'

import {
  cardLoadingStyles,
  getInsightCategoryLabel,
  getInsightCategoryOrder,
  OrgUnitContext,
  ReportProviderContext,
  TimeseriesContext,
} from '@percept/app-components'

import {
  OrgUnitProviderCategoryScoreTimeseriesDatum,
  TimeseriesHealthDatum,
  useChildOrgUnitCategoryScores,
  useOrgUnitCategoryScores,
} from '@percept/hooks'

import { BubbleChartDatum } from './BubbleChart'

import { flatten, sortBy } from 'lodash-es'

import { CampaignSetupContext } from './context'

import { CAMPAIGN_SETUP_GRANULARITY, getPreviousTimeseriesDateRange, getTimeseriesDateRange, useReportAvailability } from './lib'

import { HealthType, InsightCategory, ReportProviderV2 } from '@percept/types'
import { providerLabelMap } from '@percept/constants'


type OrgUnitCategoryDatum = {
  id: string
  name: string
  health: number | null
  category: InsightCategory
}

const responseAdapter = (
  response: OrgUnitProviderCategoryScoreTimeseriesDatum[],
  provider: ReportProviderV2
): BubbleChartDatum<OrgUnitCategoryDatum, InsightCategory>[] => {
  const overallDataByCategory: Partial<Record<InsightCategory, TimeseriesHealthDatum[]>> = {}

  const providerFilteredData = response.filter( d => d.provider === provider )

  for( const d of providerFilteredData ){
    for( const t of Object.keys(d.timeseriesByCategory) as InsightCategory[] ){
      if( t !== 'OVERALL' as InsightCategory ){
        overallDataByCategory[t] = [
          ...overallDataByCategory[t] || [],
          ...d.timeseriesByCategory[t],
        ]
      }
    }
  }

  const allData = flatten(
    Object.values(overallDataByCategory)
  )

  const latestTimestamp = Math.max(...allData.map( d => d.start.getTime()))

  return providerFilteredData.reduce( (acc, item) => {
    const { org_unit_name, org_unit_id } = item
    const categories = Object.keys(item.timeseriesByCategory) as InsightCategory[]
    categories.forEach( category => {
      if( category !== 'OVERALL' as InsightCategory ){
        item.timeseriesByCategory[category].forEach( d => {
          if( d.start.getTime() === latestTimestamp && d.health !== null ){
            acc.push({
              key: category,
              column: category,
              health: d.health,
              data: {
                id: org_unit_id,
                name: org_unit_name,
                health: d.health,
                category,
              },
            })
          }
        })
      }
    })
    return acc
  }, [] as BubbleChartDatum<OrgUnitCategoryDatum, InsightCategory>[])
}

const useStyles = makeAppStyles( theme => ({
  ...cardLoadingStyles,
  title: {
    display: 'flex',
    alignItems: 'center',
    height: 28,
  },
  titleProvider: {
    color: theme.palette.text.secondary,
    // position: 'relative',
    // top: 1,
  },
  titleSeparator: {
    color: theme.palette.text.secondary,
    margin: theme.spacing(0, 0.25),
    fontSize: 30,
  },
  cardTitleProvider: {
    marginRight: theme.spacing(1),
  },
  categoryLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 13,
    [theme.breakpoints.down('sm')]: {
      fontSize: 12,
    },
  },
  orgLink: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    marginRight: theme.spacing(2),
  },
  health: {
    marginLeft: 'auto',
    paddingLeft: theme.spacing(2),
  },
}))


export const MultiOrgProviderCampaignSetup = () => {
  const orgUnit = useContext(OrgUnitContext)

  const provider = useContext(ReportProviderContext)

  const [urlState] = useContext(CampaignSetupContext)

  const availability = useReportAvailability()
  
  const minDate = availability && availability.start
  const maxDate = availability && availability.end

  const referenceDate = (
    urlState.referenceDate === 'latest' ?
      maxDate :
      new Date(urlState.referenceDate)
  )

  const granularity = CAMPAIGN_SETUP_GRANULARITY

  const dateRange = getTimeseriesDateRange(referenceDate, granularity)

  const parentCategoryScores = useOrgUnitCategoryScores({
    org_unit_id: orgUnit.id,
    ...dateRange,
    granularity,
    enabled: !!dateRange.start,
    retry: false,
  })

  const categoryScores = useChildOrgUnitCategoryScores({
    org_unit_id: orgUnit.id,
    ...dateRange,
    granularity,
    enabled: !!dateRange.start,
    retry: false,
  })

  const previousCategoryScores = useChildOrgUnitCategoryScores({
    org_unit_id: orgUnit.id,
    ...getPreviousTimeseriesDateRange(referenceDate, granularity),
    granularity,
    enabled: !!dateRange.start,
    retry: false,
  })

  const bubbleChartData = useMemo(() => {
    return responseAdapter(categoryScores.data || [], provider)
  }, [categoryScores.data, provider])

  const previousBubbleChartData = useMemo(() => {
    return responseAdapter(previousCategoryScores.data || [], provider)
  }, [previousCategoryScores.data, provider])

  const categories = getInsightCategoryOrder(
    bubbleChartData.map( d => d.column )
  )

  const scoresByCategory = (parentCategoryScores.data || []).reduce( (acc, d) => {
    if( d.provider === provider ){
      Object.entries(d.timeseriesByCategory).forEach( ([category, data]) => {
        const health = data && data.length ? data[data.length - 1].health : null
        acc[category] = health
      })
      d.timeseriesByCategory
    }
    return acc
  }, {} as Record<string, HealthType>)

  const classes = useStyles()

  return (
    <TimeseriesContext.Provider value={{
      ...dateRange,
      minDate,
      maxDate,
      granularity,
    }}>
      <CampaignSetupView
        cardTitle='Setup Scores by Category'
        tooltipContent={
          `Distribution of ${[providerLabelMap[provider]]} setup scores by advertiser and pillar.`
        }
        isError={categoryScores.isError || parentCategoryScores.isError}
        isLoading={categoryScores.isLoading || parentCategoryScores.isLoading}
        isRefetching={categoryScores.isRefetching || parentCategoryScores.isRefetching}
        dataSignal={!!(categoryScores.data && parentCategoryScores.data)}
        columns={
          categories.map( category => ({
            key: category,
            label: (
              <Typography className={classes.categoryLabel} variant='h6'>
                { getInsightCategoryLabel(category) }
                <Health
                  value={scoresByCategory[category]}
                  color='inherit'
                  animate={false} />
              </Typography>
            ),
          }))
        }
        data={bubbleChartData}
        previousData={previousBubbleChartData}
        datumKey='id'
        getPopoverContent={({ key, data }) => (
          <>
          <Box px={2} mt={2}>
            <Typography variant='h6' className={classes.categoryLabel}>
              { getInsightCategoryLabel(key) }
            </Typography>
          </Box>
          <List>
            { sortBy(data, 'health').map( org => (
              <ListItem
                key={org.id}
                component={Link}
                className={classes.orgLink}
                button
                to={`/dashboards/${org.id}/campaign-setup`}>
                <OpenInNew fontSize='small' className={classes.icon} />
                { org.name }
                <Health
                  value={org.health}
                  color='inherit'
                  className={classes.health} />
              </ListItem>
            ))}
          </List>
          </>
        )} />
    </TimeseriesContext.Provider>
  )
}
